/**
 * Pin Vision design tokens — the single source of truth for the brand palette.
 *
 * This module has two halves, and the difference matters:
 *
 *   BUILD TOKENS      Consumed by `tailwind.config.js` (utility classes) and
 *                     `src/setup.ts` (the PrimeVue preset). Changing a value
 *                     here changes the rendered UI everywhere.
 *
 *   DOCUMENTATION     Read only by the /design-system route and the
 *                     `design-system` skill. These describe conventions the
 *                     codebase already follows by hand; nothing in the build
 *                     reads them, so editing one changes documentation, not
 *                     pixels.
 *
 * The ramps below used to be duplicated verbatim between tailwind.config.js
 * and setup.ts. They live here now so the two can't drift.
 *
 * See `.claude/skills/design-system/` for how to pick between them.
 */

/** A Tailwind-shaped 11-stop color ramp. */
export type ColorRamp = {
  50: string
  100: string
  200: string
  300: string
  400: string
  500: string
  600: string
  700: string
  800: string
  900: string
  950: string
}

// ---------------------------------------------------------------------------
// BUILD TOKENS
// ---------------------------------------------------------------------------

/**
 * Brand green — positive state, and the only real accent on the marketing site.
 * Feeds Tailwind `success-*` and the PrimeVue primitive `green` scale, which is
 * what `severity="success"` resolves to in @primeuix/themes v3.
 */
export const success: ColorRamp = {
  50: '#ecf8f0',
  100: '#d4f1de',
  200: '#aae3bf',
  300: '#7fd49f',
  400: '#6ecb91',
  500: '#5cc689', // brand-green
  600: '#4eaa75',
  700: '#408b61',
  800: '#336d4c',
  900: '#264f38',
  950: '#142d20',
}

/**
 * Brand orange — caution state only. Feeds Tailwind `warn-*` and the PrimeVue
 * primitive `orange` scale behind `severity="warn"`.
 */
export const warn: ColorRamp = {
  50: '#fff7e8',
  100: '#ffeacb',
  200: '#ffd594',
  300: '#ffc05d',
  400: '#ffb84d',
  500: '#ffb13d', // brand-orange
  600: '#d99632',
  700: '#b37b29',
  800: '#8c6020',
  900: '#664517',
  950: '#3a280c',
}

/**
 * Brand red — errors and destructive actions. Feeds Tailwind `danger-*`, the
 * PrimeVue primitive `red` scale behind `severity="danger"`, and every
 * mapbox-gl-draw style in `src/constants/map/draw-control-styles.ts`.
 */
export const danger: ColorRamp = {
  50: '#fde9ea',
  100: '#fad4d4',
  200: '#f4a9aa',
  300: '#ef7d7e',
  400: '#ec5a5b',
  500: '#ea4243', // brand-red
  600: '#c63839',
  700: '#a32d2e',
  800: '#802324',
  900: '#5d191a',
  950: '#350e0e',
}

/**
 * Info blue — "live", "transmitting", "in progress". The only cool accent in a
 * warm palette, which is what makes a moving device read as moving. Feeds
 * Tailwind `info-*` and the PrimeVue primitive `sky` scale behind
 * `severity="info"`; the stops are Tailwind's `sky`, which is the ramp Aura
 * already resolves that severity to, so the two cannot disagree.
 */
export const info: ColorRamp = {
  50: '#f0f9ff',
  100: '#e0f2fe',
  200: '#bae6fd',
  300: '#7dd3fc',
  400: '#38bdf8',
  500: '#0ea5e9', // info-blue
  600: '#0284c7',
  700: '#0369a1',
  800: '#075985',
  900: '#0c4a6e',
  950: '#082f49',
}

/**
 * Brand ink — a near-black desaturated slate-teal. This is body text, dark UI
 * chrome, the sidebar active state and the inverted marketing bands; it is not
 * a "blue".
 *
 * Consumed by `semantic.primary` in the PrimeVue preset. Tailwind's `primary-*`
 * utilities are generated from that preset by `tailwindcss-primeui` as
 * `var(--p-primary-*)`, so this ramp is NOT declared in tailwind.config.js.
 */
export const primary: ColorRamp = {
  50: '#e6e9eb',
  100: '#cfd5da',
  200: '#a9b3bb',
  300: '#7b8c99',
  400: '#556776',
  500: '#203036', // brand-primary
  600: '#1c2a30',
  700: '#162229',
  800: '#111b22',
  900: '#0b1319',
  950: '#050a0e',
}

/**
 * Neutrals — Tailwind slate's lightness and saturation, rotated onto the brand
 * ink's hue (196°). Page and panel backgrounds, borders, dividers, and muted text.
 *
 * Replaces Aura's own `surface`, which is Tailwind slate: ~120 Aura tokens (text,
 * borders, fills — secondary buttons, toasts, tags, form fields…) reference
 * `{surface.N}`, so overriding the palette here is what keeps slate out of the UI.
 * This is PrimeVue's documented way to change the neutral tone; never override
 * those component tokens one by one.
 *
 * Consumed by `semantic.surface` in the PrimeVue preset; Tailwind's `surface-*`
 * utilities come from that preset via `tailwindcss-primeui`, like `primary-*`.
 */
export const surface: ColorRamp & { 0: string } = {
  0: '#ffffff',
  50: '#f8fbfc',
  100: '#f1f7f9',
  200: '#e2ecf0',
  300: '#cbdbe1',
  400: '#94aeb8',
  500: '#64808b',
  600: '#476069',
  700: '#334c55',
  800: '#1e333b',
  900: '#0f232a',
  950: '#021117',
}

/**
 * The one brand tint with no palette equivalent. It has exactly one job: the
 * `bg-brand-secondary/10` tile behind a KPI icon, which is now owned by
 * `@/components/ui/KpiCard.vue`. Reach for the component, not the class.
 */
export const brandSecondary = '#3f5f6b'

/**
 * Colours consumed from JavaScript — Mapbox paint expressions, canvas fills, and
 * the handful of scoped rules that build a pulse keyframe out of a fill.
 *
 * A Tailwind class cannot reach those places, which is why the map subsystem
 * accumulated stock-Tailwind hex (`#10B981`, `#3B82F6`, `#F59E0B`) sitting a
 * visible step off the brand ramp. Every value below is a stop from a ramp
 * above — this map adds names, never new colours.
 *
 * **Never write a colour literal in a component.** If you need one from JS, it
 * belongs here.
 */
export const statusColors = {
  /**
   * Live, connected, healthy. Blue rather than the brand green: on the map this
   * means "transmitting right now", and green reads as "fine" rather than
   * "moving".
   */
  online: info[500],
  /** Not live. Reads as "inactive", not "broken" — deliberately desaturated. */
  offline: primary[300],
  /** Needs attention but still working. */
  warning: warn[500],
  /** Faulted, expired, disconnected. */
  error: danger[500],
  /** The current selection on a map. */
  selected: primary[500],
  /** Unknown or uncategorised. */
  neutral: primary[200],
  /** Marker cores, cluster labels, and anything sitting on a dark fill. */
  onDark: '#ffffff',
} as const

export type StatusColor = keyof typeof statusColors

/**
 * Pace-of-play verdict: is a player on target, drifting, or late? Same
 * green/amber/red triple the battery bar already uses, named once so the
 * timeline dot and the map marker ring cannot drift apart — the ring needs the
 * value from JS, which a Tailwind class cannot give it.
 */
export const paceColors = {
  onPace: success[500],
  slightlyBehind: warn[500],
  behind: danger[500],
} as const

/**
 * Adieu is self-hosted (`src/assets/fonts/`, @font-face'd in
 * `src/assets/styles/root.scss` at 400 and 700 — the only two cuts we ship)
 * and carries headings. Montserrat is the body font, bundled from
 * `@fontsource-variable/montserrat` (imported in `src/main.ts`) so no
 * render-blocking request goes to Google Fonts.
 */
export const fontFamily = {
  sans: ['Montserrat Variable', 'sans-serif'],
  adieu: ['Adieu', 'sans-serif'],
  mono: ['ui-monospace', 'SFMono-Regular', 'Menlo', 'Monaco', 'Consolas', 'monospace'],
}

// ---------------------------------------------------------------------------
// DOCUMENTATION TOKENS — described, not enforced
// ---------------------------------------------------------------------------

/**
 * Radius reads as a depth cue in this app: the further a surface is from the
 * data, the rounder it gets. These are stock Tailwind values — the config
 * overrides none of them — but the *assignment* below is a real convention.
 */
export const radiusTiers = [
  { token: 'rounded-lg', value: '0.5rem', use: 'Buttons, inputs, selects' },
  { token: 'rounded-xl', value: '0.75rem', use: 'Sidebar rows, admin nav items' },
  { token: 'rounded-2xl', value: '1rem', use: 'Admin panels and cards, toolbars, KPI icon tiles' },
  { token: 'rounded-3xl', value: '1.5rem', use: 'Marketing cards, the floating site topbar' },
  { token: 'rounded-full', value: '9999px', use: 'Pills, avatars, glow blobs, CTA buttons' },
] as const

/**
 * Surfaces rest flat — a 1px `surface-200` border on the marketing site, nothing
 * at all in the admin — and lift on interaction: borders over shadows. Each
 * shell has exactly one lift, and in the admin it is opt-in through
 * `.interactive`, which works on any surface (Panel or Card).
 */
export const elevationTiers = [
  {
    context: 'Marketing card',
    rest: 'border border-surface-200',
    hover: 'hover:shadow-xl hover:-translate-y-1',
  },
  {
    context: 'Admin panel or card',
    rest: 'border-none shadow-none',
    hover: 'hover:shadow-2xl (only with .interactive)',
  },
  { context: 'DataTable', rest: 'shadow-sm', hover: 'row hover:bg-surface-50' },
] as const

/**
 * One-sided fades from a semantic tint to transparent — for flagging a row's
 * status without a hard block of colour. Defined in
 * `src/assets/styles/components/_common.scss` under `STATUS FADE`. Reach for
 * these before hand-rolling `bg-gradient-to-r from-*-100 to-transparent` on a
 * custom row.
 */
export const statusFades = [
  {
    className: 'bg-fade-success',
    from: 'success-100',
    use: 'Own-club row inside the ranking — positive state, no hard fill',
  },
  {
    className: 'bg-fade-danger',
    from: 'danger-100',
    use: 'Own-club row appended outside the ranking — alert state, no hard fill',
  },
] as const

/**
 * The severity vocabulary as actually used, with the invalid combinations
 * flagged rather than quietly corrected. See
 * `.claude/skills/design-system/references/known-drift.md`.
 */
export const severityVocabulary = [
  { value: 'secondary', uses: 50, valid: true },
  { value: 'danger', uses: 45, valid: true },
  { value: 'primary', uses: 18, valid: true, note: 'Not a valid Tag severity, only Button/Badge' },
  { value: 'success', uses: 16, valid: true },
  {
    value: 'error',
    uses: 14,
    valid: true,
    note: 'Message only. Every current use is on Message; invalid anywhere else',
  },
  { value: 'info', uses: 3, valid: true },
  { value: 'warn', uses: 3, valid: true },
  { value: 'contrast', uses: 0, valid: true, note: 'Declared in unions but unused in templates' },
] as const
